import { useState, useEffect } from 'react';

interface Coordinates {
  latitude: number;
  longitude: number;
  accuracy?: number;
}

export function useGeolocation(options?: PositionOptions) {
  const [coordinates, setCoordinates] = useState<Coordinates | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!navigator.geolocation) {
      setError('Geolocation is not supported');
      setIsLoading(false);
      return;
    }

    // Set a timeout to handle cases where the response takes too long
    const timeoutId = setTimeout(() => {
      setError('Geolocation request timed out');
      setIsLoading(false);
    }, options?.timeout || 10000); // Default 10 second timeout

    const successHandler = (position: GeolocationPosition) => {
      clearTimeout(timeoutId); // Clear the timeout if successful
      const { latitude, longitude, accuracy } = position.coords;
      setCoordinates({ latitude, longitude, accuracy });
      setIsLoading(false);
    };

    const errorHandler = (error: GeolocationPositionError) => {
      clearTimeout(timeoutId); // Clear the timeout on error
      setError(error.message);
      setIsLoading(false);
    };

    // Get position just once (no watchPosition)
    navigator.geolocation.getCurrentPosition(
      successHandler,
      errorHandler,
      {
        enableHighAccuracy: true,
        timeout: 10000, // 10 seconds
        maximumAge: 0, // No cached position
        ...options // Allow custom options to override
      }
    );

    return () => {
      clearTimeout(timeoutId); // Cleanup timeout on unmount
    };
  }, []); // Empty dependency array ensures this runs only once on mount

  return { coordinates, error, isLoading };
}